// 🔴 PRIMITIVE CORE — RECHERCHE D'ENTREPRISE au registre public.
//
// ⚠️ POURQUOI ELLE EXISTE MAINTENANT. Mesuré : `recherche-entreprises.api.gouv.fr`
// est DÉJÀ appelé — mais DUPLIQUÉ EN LIGNE dans au moins trois écrans
// (`HubSettings`, `ProspectsList`, `InstallateursManager`), chacun avec sa
// propre forme de requête et son propre mapping. ⛔ Aucun composant partagé.
// 🔑 ⇒ Ce ⛔ n'est PAS « coder une recherche SIRET pour Mario » : c'est EXTRAIRE
// la capacité qui existait déjà en trois exemplaires. Mario s'en sert
// aujourd'hui ; les trois écrans historiques convergeront dans un lot
// transverse — ⛔ pas aujourd'hui, la démo est demain.
//
// 🔑 MÊME CONTRAT QUE `BanAddressInput` : { value, onChange, onPick } — pour
// qu'un écran qui sait poser une adresse sache poser une entreprise.
import React, { useEffect, useRef, useState } from "react";

export default function EntrepriseSearchInput({ value, onChange, onPick,
                                                placeholder, testId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [cherche, setCherche] = useState(false);
  const minuteur = useRef(null);

  useEffect(() => () => clearTimeout(minuteur.current), []);

  const interroger = (q) => {
    clearTimeout(minuteur.current);
    const propre = (q || "").trim();
    // ⚠️ En dessous de 3 caractères le registre rend du bruit : on ⛔ n'affiche
    // RIEN plutôt qu'une liste qui ⛔ ne veut rien dire.
    if (propre.length < 3) { setSuggestions([]); return; }
    minuteur.current = setTimeout(async () => {
      setCherche(true);
      try {
        const r = await fetch(
          "https://recherche-entreprises.api.gouv.fr/search?q="
          + encodeURIComponent(propre) + "&per_page=5");
        const d = await r.json();
        // 🔑 On NORMALISE ici, UNE FOIS — c'est tout l'objet d'une primitive :
        // les trois écrans historiques mappaient chacun à leur façon.
        setSuggestions((d.results || []).map((x) => ({
          siren: x.siren,
          siret: x.siege?.siret || "",
          nom: x.nom_complet || x.nom_raison_sociale || "",
          forme_juridique: x.nature_juridique || "",
          adresse: x.siege?.adresse || "",
          code_postal: x.siege?.code_postal || "",
          ville: x.siege?.libelle_commune || "",
        })).filter((x) => x.nom));
      } catch {
        // ⚠️ Le registre est un service EXTERNE : son indisponibilité ⛔ ne doit
        // PAS bloquer la saisie. On vide les suggestions, la frappe continue.
        setSuggestions([]);
      } finally {
        setCherche(false);
      }
    }, 350);
  };

  return (
    <div style={{ position: "relative" }}>
      <input value={value ?? ""} aria-label={placeholder || "Entreprise"}
             placeholder={placeholder || "Nom, SIREN ou SIRET"}
             data-testid={testId || "entreprise-recherche"}
             onChange={(e) => { onChange(e.target.value); interroger(e.target.value); }}
             style={{ height: 36, borderRadius: 8, padding: "0 10px",
                      border: "1px solid var(--oh-border)",
                      background: "var(--oh-bg-surface)",
                      color: "var(--oh-text)", width: "100%" }} />
      {cherche && (
        <div style={{ fontSize: 11, color: "var(--oh-text-muted)",
                      marginTop: 4 }}>Recherche…</div>
      )}
      {suggestions.length > 0 && (
        <ul style={{ position: "absolute", zIndex: 20, left: 0, right: 0,
                     margin: "4px 0 0", padding: 0, listStyle: "none",
                     background: "var(--oh-bg-elevated)",
                     border: "0.5px solid var(--oh-border)", borderRadius: 8,
                     overflow: "hidden" }}
            data-testid="entreprise-suggestions">
          {suggestions.map((s) => (
            <li key={s.siret || s.siren}>
              <button type="button"
                      onClick={() => { onPick && onPick(s); setSuggestions([]); }}
                      data-testid="entreprise-suggestion"
                      style={{ display: "block", width: "100%",
                               textAlign: "left", padding: "8px 10px",
                               background: "transparent", border: "none",
                               color: "var(--oh-text)", fontSize: 13 }}>
                <div style={{ fontWeight: 500 }}>{s.nom}</div>
                <div style={{ fontSize: 11, color: "var(--oh-text-secondary)" }}>
                  {s.siret || s.siren}
                  {s.ville ? ` · ${s.code_postal} ${s.ville}` : ""}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
